import * as React from "react";
import { usePopupContext } from "src/hooks/usePopup";
import { attributeToTitle } from "src/utils/strings";
import { useEntitySelectors } from "src/search/useEntitySelectors";
import { SelectInputClause, UseQuestionInputClauses } from "./definitions";
import { Clause } from "./Clause";
import { SelectIcon } from "src/components/SelectIcon";
import { theme, Space, Select, Tooltip } from "antd";
import styles from "./question-input.module.css";
import { FieldTooltip } from "./ClauseTooltips";
import { entityDefinition } from "@vzbl/integrations";
import { useIntegrationConfig } from "src/search/lib/useIntegrationConfig";
import { IntegrationIcon } from "src/components/IntegrationIcon";
import { Flex } from "src/components/Flex";
import { CustomTooltip } from "src/components/CustomTooltip";

// Panel component that handles its own state independently
function SelectPanel({
  initialValue,
  value,
  onApply,
  onCancel,
}: {
  initialValue?: string;
  value: any;
  onApply: (entity: string) => void;
  onCancel: () => void;
}) {
  const { token } = theme.useToken();
  const [selectedEntity, setSelectedEntity] = React.useState<
    string | undefined
  >(initialValue);
  const { selectableEntities } = useEntitySelectors({ state: value });

  // Reset entity when panel opens with new initial value
  React.useEffect(() => {
    setSelectedEntity(initialValue);
  }, [initialValue]);

  // we use integration config to decorate the entities in tooltip
  const integrationConfig = useIntegrationConfig();

  type Integration = {
    name: string;
    provides: string[];
  };

  const entityIntegrations = React.useMemo(() => {
    if (!integrationConfig.data) return {};
    const ret: Record<string, string[]> = {};
    (integrationConfig.data as any).integrations.forEach((i: Integration) => {
      i.provides
        .filter((i) => i != "vzbl")
        .forEach((entity) => {
          if (!ret[entity]) ret[entity] = [];
          ret[entity].push(i.name);
        });
    });
    return ret;
  }, [integrationConfig]);

  // Format entity options
  const entityOptions = React.useMemo(
    () =>
      selectableEntities
        .sort((a, b) => a.localeCompare(b))
        .map((entity) => {
          const integrations = entityIntegrations[entity] || [];
          const exampleFields = entityDefinition(entity)?.exampleFields;
          const title = (
            <Flex gap={4} direction="column">
              <Flex gap={6}>
                {entityDefinition(entity)?.description}
                <div>
                  {integrations.map((i) => (
                    <div
                      style={{
                        display: "inline-block",
                        height: 28,
                        marginRight: 4,
                        borderRadius: 4,
                        // padding: 2,
                        filter: "grayscale(100%)",
                      }}
                      key={i}
                    >
                      <IntegrationIcon source={i} size={20} />
                    </div>
                  ))}
                </div>
              </Flex>
              {exampleFields ? (
                <div
                  style={{ fontSize: 12, color: "#666", fontStyle: "italic" }}
                >
                  {exampleFields.join(", ")}, ...
                </div>
              ) : null}
            </Flex>
          );

          return {
            value: entity,
            label: (
              <CustomTooltip content={title} placement="right">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span>{attributeToTitle(entity)}</span>
                </div>
              </CustomTooltip>
            ),
          };
        }),
    [selectableEntities, entityIntegrations]
  );

  // Enhanced search function
  const searchEntity = React.useCallback((input: string, option: any) => {
    if (!input.trim()) return true;
    const searchableText = attributeToTitle(option.value).toLowerCase();
    const searchTerms = input.toLowerCase().split(/\s+/);
    return searchTerms.every((term) => searchableText.includes(term));
  }, []);

  return (
    <div className={styles.clausePanel} onClick={(e) => e.stopPropagation()}>
      <Space direction="vertical" style={{ width: "300px" }} size="large">
        {/* Entity Selection */}
        <div>
          <FieldTooltip type="entity">
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: token.colorTextSecondary,
                marginBottom: 8,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Entity
            </div>
          </FieldTooltip>
          <Select
            style={{ width: "100%" }}
            showSearch
            placeholder="Search for an entity..."
            value={selectedEntity}
            onChange={setSelectedEntity}
            options={entityOptions}
            filterOption={searchEntity}
            optionFilterProp="label"
            defaultOpen={true}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            paddingTop: 16,
            marginTop: 8,
          }}
        >
          <div
            style={{
              fontSize: "0.85em",
              color: token.colorTextDescription,
            }}
          >
            {(() => {
              if (!selectedEntity) return "Select an entity to query";
              return "Ready to apply selection";
            })()}
          </div>
          <Space>
            <button
              onClick={onCancel}
              style={{
                background: "none",
                border: "none",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                color: "rgba(0, 0, 0, 0.65)",
                fontSize: 14,
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.04)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              Cancel
            </button>
            <button
              style={{
                fontSize: 14,
                backgroundColor: "#1677ff",
                color: "white",
                border: "none",
                padding: "5px 16px",
                borderRadius: 6,
                cursor: selectedEntity ? "pointer" : "not-allowed",
                opacity: selectedEntity ? 1 : 0.5,
                transition: "all 0.2s",
                boxShadow: "0 2px 0 rgba(5, 145, 255, 0.1)",
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = "#4096ff";
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = "#1677ff";
                }
              }}
              disabled={!selectedEntity}
              onClick={() => selectedEntity && onApply(selectedEntity)}
            >
              Apply
            </button>
          </Space>
        </div>
      </Space>
    </div>
  );
}

export function SelectClause({
  clause,
  value,
  removeClause,
  updateClause,
}: {
  clause: SelectInputClause;
  value: any;
  updateClause: (clause: any) => void;
  removeClause: (clause: any) => void;
}) {
  const popup = usePopupContext();

  const handleApply = (entity: string) => {
    updateClause({
      ...clause,
      value: entity,
    });
    popup.close();
  };

  return (
    <Clause
      onRemove={() => removeClause(clause)}
      onMenuClose={() => popup.close()}
      renderMenu={() => (
        <SelectPanel
          initialValue={clause.value}
          onApply={handleApply}
          onCancel={() => popup.close()}
          value={value}
        />
      )}
      clause={clause}
      title="Select"
      icon={<SelectIcon size={10} style={{ opacity: 0.7 }} />}
    >
      {clause.value ? attributeToTitle(clause.value) : null}
    </Clause>
  );
}
